import React, { useState } from 'react';
import {
  interpRuntimeConfig,
  INTERP_PRESETS,
  type InterpEntityType,
} from '../../systems/network/interpolationConfig';
import type { InterpMode } from '../../systems/network/SnapshotInterpolator';
import { mobBumpConfig, setMobBump, resetMobBump } from '../../systems/entity/mobBumpConfig';

/**
 * InterpolationTuningControls — the live interp/feel tuning UI, surface-agnostic
 * (smoothness arc Phase 3/6, 2026-05-29). Rendered by BOTH the DiagnosticHub
 * tab AND the in-game debug-tool overlay (DebugHUDHost). The in-game surface is
 * the one that actually live-tunes — it shares the game's JS context, so
 * writing interpRuntimeConfig/mobBumpConfig here re-tunes the running
 * interpolators on the next frame. The standalone /diagnostic tab is for
 * inspection (its sliders have no local game to affect).
 */

const LAG_MIN = 40, LAG_MAX = 200, CLAMP_MIN = 1, CLAMP_MAX = 4;

export const tuningBtn = (active: boolean): React.CSSProperties => ({
  padding: '5px 9px',
  borderRadius: 5,
  border: `1px solid ${active ? '#42d77d' : '#444'}`,
  background: active ? '#42d77d22' : '#1c1c22',
  color: active ? '#42d77d' : '#bbb',
  fontSize: 11,
  cursor: 'pointer',
});

const TypeControls: React.FC<{ type: InterpEntityType; bump: () => void }> = ({ type, bump }) => {
  const cfg = interpRuntimeConfig.get(type);
  const setMode = (mode: InterpMode) => { interpRuntimeConfig.set(type, { mode }); bump(); };
  return (
    <div data-testid={`interp-controls-${type}`} style={{ marginBottom: 10 }}>
      <div style={{ fontWeight: 700, fontSize: 11, color: '#ddd', marginBottom: 4, textTransform: 'capitalize' }}>{type}</div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
        <button style={tuningBtn(cfg.mode === 'hermite')} onClick={() => setMode('hermite')} data-testid={`interp-${type}-hermite`}>hermite</button>
        <button style={tuningBtn(cfg.mode === 'linear')} onClick={() => setMode('linear')} data-testid={`interp-${type}-linear`}>linear</button>
      </div>
      <label style={{ display: 'block', fontSize: 10, color: '#aaa' }}>
        render-lag {cfg.renderLagMs}ms
        <input type="range" min={LAG_MIN} max={LAG_MAX} step={5} value={cfg.renderLagMs}
          data-testid={`interp-${type}-lag`}
          onChange={(e) => { interpRuntimeConfig.set(type, { renderLagMs: Number(e.target.value) }); bump(); }}
          style={{ width: '100%' }} />
      </label>
      <label style={{ display: 'block', fontSize: 10, color: '#aaa' }}>
        tangent clamp {cfg.tangentClampFactor.toFixed(1)} {cfg.mode === 'linear' ? '(unused in linear)' : ''}
        <input type="range" min={CLAMP_MIN} max={CLAMP_MAX} step={0.5} value={cfg.tangentClampFactor}
          data-testid={`interp-${type}-clamp`}
          onChange={(e) => { interpRuntimeConfig.set(type, { tangentClampFactor: Number(e.target.value) }); bump(); }}
          style={{ width: '100%' }} />
      </label>
    </div>
  );
};

export const InterpolationTuningControls: React.FC = () => {
  const [, setTick] = useState(0);
  const bump = () => setTick((n) => n + 1);

  return (
    <div data-testid="interp-tuning-controls" style={{ fontSize: 11, color: '#ccc' }}>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: '#bbb', marginBottom: 4 }}>Presets</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {INTERP_PRESETS.map((p) => (
            <button key={p.id} data-testid={`interp-preset-${p.id}`} title={p.description} style={tuningBtn(false)}
              onClick={() => { interpRuntimeConfig.applyPreset(p.id); bump(); }}>
              {p.label}
            </button>
          ))}
          <button data-testid="interp-reset" style={tuningBtn(false)} onClick={() => { interpRuntimeConfig.reset(); bump(); }}>reset</button>
        </div>
      </div>

      <TypeControls type="player" bump={bump} />
      <TypeControls type="mob" bump={bump} />

      <div data-testid="mob-bump-controls" style={{ borderTop: '1px solid #333', paddingTop: 8, marginBottom: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 11, color: '#ddd', marginBottom: 4 }}>
          Mob bump feel <span style={{ color: '#666', fontWeight: 400 }}>(walk into a mob)</span>
        </div>
        <label style={{ display: 'block', fontSize: 10, color: '#aaa' }}>
          resolve {mobBumpConfig.resolve.toFixed(2)} <span style={{ color: '#666' }}>(overlap pushed off you / frame)</span>
          <input type="range" min={0} max={1} step={0.05} value={mobBumpConfig.resolve} data-testid="mobbump-resolve"
            onChange={(e) => { setMobBump({ resolve: Number(e.target.value) }); bump(); }} style={{ width: '100%' }} />
        </label>
        <label style={{ display: 'block', fontSize: 10, color: '#aaa' }}>
          damp {mobBumpConfig.damp.toFixed(2)} <span style={{ color: '#666' }}>(velocity kept; 0=dead stop)</span>
          <input type="range" min={0} max={1} step={0.05} value={mobBumpConfig.damp} data-testid="mobbump-damp"
            onChange={(e) => { setMobBump({ damp: Number(e.target.value) }); bump(); }} style={{ width: '100%' }} />
        </label>
        <label style={{ display: 'block', fontSize: 10, color: '#aaa' }}>
          jitter {mobBumpConfig.randomPx.toFixed(0)}px <span style={{ color: '#666' }}>(organic randomness)</span>
          <input type="range" min={0} max={20} step={1} value={mobBumpConfig.randomPx} data-testid="mobbump-jitter"
            onChange={(e) => { setMobBump({ randomPx: Number(e.target.value) }); bump(); }} style={{ width: '100%' }} />
        </label>
        <button style={tuningBtn(false)} data-testid="mobbump-reset" onClick={() => { resetMobBump(); bump(); }}>reset bump</button>
      </div>

      <div style={{ fontSize: 10, color: '#888' }}>
        config v{interpRuntimeConfig.version}. Live in-game (no reload). Watch the effect on the Smoothness Dashboard
        interp readout. Launch seeds: <code>?interp=linear|hermite</code>, <code>?playerLag=NN</code>, <code>?mobLag=NN</code>.
      </div>
    </div>
  );
};

/** Copy-to-clipboard text for the current config (shared by tab + overlay). */
export function interpConfigCopyText(): string {
  const p = interpRuntimeConfig.player, m = interpRuntimeConfig.mob;
  return [
    `INTERP CONFIG (v${interpRuntimeConfig.version})`,
    `player: ${p.mode} · lag ${p.renderLagMs}ms · clamp ${p.tangentClampFactor} · ring ${p.ringCapacity}`,
    `mob:    ${m.mode} · lag ${m.renderLagMs}ms · clamp ${m.tangentClampFactor} · ring ${m.ringCapacity}`,
    `bump:   resolve ${mobBumpConfig.resolve} · damp ${mobBumpConfig.damp} · jitter ${mobBumpConfig.randomPx}px`,
  ].join('\n');
}
